import { signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { createNotifier } from 'ngxtension/create-notifier';
import {
  Observable,
  combineLatest,
  debounceTime,
  defer,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs';
import { DataProvider, DataProviderConfig, EntityType } from './types';

type LoadFn<Entity> = (search: string) => Observable<Entity[]>;

export function remoteDataProvider<Entity extends EntityType>(
  loadFn: LoadFn<Entity>,
  config?: DataProviderConfig<Entity>,
): DataProvider<Entity> {
  const search = signal('');
  const loading = signal(false);
  const refresh = createNotifier();

  const _data = toSignal(
    combineLatest([toObservable(search), toObservable(refresh.listen)]).pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map(([value]) => value),
      switchMap((value) =>
        defer(() => {
          loading.set(true);
          return loadFn(value);
        }).pipe(
          tap(() => {
            loading.set(false);
          }),
        ),
      ),
    ),
    { initialValue: [] },
  );

  return {
    loading,
    data: _data,
    supportSearch: true,
    setSearch(value) {
      search.set(value);
    },
    refresh() {
      refresh.notify();
    },
    config: {
      valueKey: 'value',
      labelKey: 'label',
      ...config,
    },
  };
}
